/**
 * Localises and reformats Service Attendance check-in / check-out history notes.
 *
 * These notes are rendered server-side (BFF) and stored with UTC, full-datetime
 * timestamps baked into the text. The history endpoint returns only the rendered
 * string plus the log's `time` (its createdAt) — the raw per-event timestamps are
 * not sent. This helper rewrites attendance notes at render time so the times show
 * in the viewer's local timezone as `HH:mm`, matching the History TIME column
 * (which uses the same `toLocaleTimeString('en-AU', { hour12: false })`).
 *
 *   Check-in:  "Checked-in: Travel Began: 2026-03-19 00:47, Onsite at: 2026-06-10 06:28. notes"
 *           -> "Checked-in: Travel Began: 11:47, Onsite at: 16:28. notes"
 *   Check-out: "Checked-out: notes"
 *           -> "Checked-out: Offsite at: 15:42. notes"   (offsite time = the log's `time`)
 *
 * Non-attendance notes are returned untouched. If a check-in note doesn't match the
 * expected format (e.g. the backend wording changes), the original string is returned
 * unchanged — the worst case is the old note, never a crash or a wrong-looking time.
 */

/** Local time formatter, identical to the History TIME column. */
const toLocalTime = (date: Date): string =>
  date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

/** Matches a "YYYY-MM-DD HH:mm" (optionally with seconds) datetime embedded in text. */
const EMBEDDED_DATETIME = /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/g;

/** Non-global tester for the same pattern (kept separate to avoid shared lastIndex state). */
const HAS_DATETIME = /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

/** Replace every embedded UTC datetime with its local `HH:mm`; unmatched text is left intact. */
const localiseEmbeddedTimes = (text: string): string =>
  text.replace(EMBEDDED_DATETIME, (full, datePart, timePart, seconds) => {
    const date = new Date(`${datePart}T${timePart}${seconds ?? ':00'}Z`);
    return isNaN(date.getTime()) ? full : toLocalTime(date);
  });

/** Convert an ISO timestamp to local `HH:mm`, or null if it isn't a valid date. */
const localTimeFromIso = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(String(value));
  return isNaN(date.getTime()) ? null : toLocalTime(date);
};

export const formatAttendanceNote = (
  note: string,
  logTypeCode?: unknown,
  logTime?: unknown
): string => {
  if (typeof note !== 'string' || !note) return '';

  const code = String(logTypeCode ?? '').toLowerCase();
  const isCheckIn = code.includes('checkin') || code.includes('check-in');
  const isCheckOut = code.includes('checkout') || code.includes('check-out');

  if (isCheckIn) {
    return localiseEmbeddedTimes(note);
  }

  if (isCheckOut) {
    // A future backend change may embed the offsite datetime directly — localise it in place.
    if (HAS_DATETIME.test(note)) {
      return localiseEmbeddedTimes(note);
    }
    const offsite = localTimeFromIso(logTime);
    if (!offsite) return note;
    const match = note.match(/^(\s*Checked-out:\s*)([\s\S]*)$/i);
    if (!match) return note;
    const trailing = match[2].trim();
    return `Checked-out: Offsite at: ${offsite}.${trailing ? ` ${trailing}` : ''}`;
  }

  return note;
};
